import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Modal, Skeleton } from '../ui';
import './HistoricalAnalysisV2.css';

const money = cents =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(cents || 0) / 100
  );
const number = value =>
  new Intl.NumberFormat('en-US').format(Number(value || 0));
// Historical windows include their start and exclude their end, in UTC.
const boundaryDate = value => {
  if (!value) return '—';
  const boundary = new Date(value);
  const hasTime =
    boundary.getUTCHours() ||
    boundary.getUTCMinutes() ||
    boundary.getUTCSeconds() ||
    boundary.getUTCMilliseconds();
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    ...(hasTime ? { timeStyle: 'medium' } : {}),
    timeZone: 'UTC'
  }).format(boundary);
};
const historyRange = item =>
  `${boundaryDate(item.startDate)} – before ${boundaryDate(item.endDate)}`;
const expiry = value =>
  value
    ? `${new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'UTC'
      }).format(new Date(value))} UTC`
    : '—';
const deduction = cents =>
  `${Number(cents || 0) > 0 ? '−' : ''}${money(cents)}`;

const PANEL = 'min-w-0 shadow-none dark:border-strokedark dark:bg-boxdark';
const MUTED = 'text-body dark:text-bodydark';
const SECONDARY_BUTTON =
  'dark:border-strokedark dark:bg-boxdark dark:text-bodydark1 dark:hover:bg-meta-4';
const EXCLUSION_MESSAGES = {
  not_found: 'This repository is no longer available to GitMe.',
  access_unavailable:
    'GitMe does not currently have access to this repository.',
  history_unavailable: 'Historical data is not available for this repository.',
  connection_needs_review: 'The repository connection needs a quick review.',
  temporary_unavailable:
    'This repository could not be reached after several attempts.'
};
const REPOSITORY_STATUSES = {
  pending: 'Waiting',
  queued: 'Waiting',
  scanning: 'Scanning',
  running: 'Scanning',
  processing: 'Scanning',
  retrying: 'Retrying',
  completed: 'Scanned',
  excluded: 'Not included',
  failed: 'Needs attention'
};

const progressPercentage = (current, total) =>
  Number(total) > 0
    ? Math.min(
        100,
        Math.max(0, Math.round((Number(current || 0) / total) * 100))
      )
    : null;

const renderProgress = (current, total, label) => {
  const percent = progressPercentage(current, total);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent ?? undefined}
      className="mt-4 h-2 overflow-hidden rounded-full bg-muted dark:bg-meta-4"
    >
      <div
        className={`h-full rounded-full bg-primary ${percent === null ? 'animate-pulse motion-reduce:animate-none' : ''}`}
        style={{ width: percent === null ? '25%' : `${percent}%` }}
      />
    </div>
  );
};

const HistoricalAnalysisV2View = ({
  loading,
  request,
  offer,
  creditCents,
  selectedMonths,
  reviewOpen,
  busy,
  excludedRepositories,
  supportUrl,
  onPrepare,
  onPurchase,
  onSelect,
  onReview,
  onChangePeriod
}) => {
  const groupId = useId();
  const preparing = ['pending_estimate', 'estimating'].includes(
    request?.status
  );
  const offersReady = ['estimate_ready', 'payment_failed'].includes(
    request?.status
  );
  const paymentPending = request?.status === 'payment_pending';
  const processing = ['processing_queued', 'processing'].includes(
    request?.status
  );
  const hasBalance = creditCents > 0;
  const discovery = request?.discoveryProgress;
  const discoveringRepositories = Number(discovery?.total) > 0;
  const allRepositoriesExcluded =
    discovery?.repositories?.length > 0 &&
    discovery.repositories.every(repo => repo.status === 'excluded');
  const pipeline = request?.pipelineProgress;
  const phases = pipeline
    ? [
        {
          label: 'Preparing commit data',
          current: pipeline.preprocessedCommits,
          total: pipeline.totalCommits,
          unit: 'prepared'
        },
        {
          label: 'Effort analysis',
          current: pipeline.effort?.submittedCommits,
          total: pipeline.effort?.totalCommits,
          unit: 'submitted'
        },
        {
          label: 'Categorization',
          current: pipeline.categorization?.submittedCommits,
          total: pipeline.categorization?.totalCommits,
          unit: 'submitted'
        },
        {
          label: 'AI leverage analysis',
          current: pipeline.aiLeverage?.submittedCommits,
          total: pipeline.aiLeverage?.totalCommits,
          unit: 'submitted'
        },
        {
          label: 'Finalizing commits',
          current:
            Number(pipeline.finalizing?.completedCommits || 0) +
            Number(pipeline.finalizing?.failedCommits || 0),
          total: pipeline.finalizing?.totalCommits,
          unit: 'resolved'
        }
      ]
    : [];
  const completed = Number(request?.progress?.completedCommits || 0);
  const failed = Number(request?.progress?.failedCommits || 0);
  const total = Number(request?.progress?.totalCommits || 0);
  const resolved = completed + failed;
  const step =
    request?.status === 'completed'
      ? 4
      : paymentPending
        ? 2
        : processing || request?.status === 'processing_failed'
          ? 3
          : offersReady
            ? reviewOpen && offer
              ? 2
              : 1
            : request?.status === 'expired'
              ? 1
              : 0;

  const excludedNotice = excludedRepositories.length > 0 && (
    <Card
      className={`${PANEL} border-warning/30 bg-warning/5`}
      aria-hidden={reviewOpen || undefined}
      inert={reviewOpen ? '' : undefined}
    >
      <h2 className="text-base font-semibold">
        Some repositories were not included
      </h2>
      <p className={`mt-1 text-sm ${MUTED}`}>
        {offersReady
          ? 'Your offer includes only accessible repository history. You can still review and purchase an offer.'
          : request?.status === 'no_offers'
            ? 'No offer is available. You can ask support to review access to the repositories below.'
            : 'These repositories are not included. You can ask support to review their access.'}
      </p>
      <details className="mt-3">
        <summary className="cursor-pointer text-sm font-medium text-primary focus-visible:outline-primary">
          View {number(excludedRepositories.length)} excluded repositories
        </summary>
        <ul className="mt-3 space-y-3 text-sm">
          {excludedRepositories.map(repo => (
            <li key={repo.repoUid || repo.repoName}>
              <p className="font-medium">{repo.repoName}</p>
              <p className={MUTED}>
                {EXCLUSION_MESSAGES[repo.exclusionReason] ||
                  'This repository was not included in the offer.'}
              </p>
            </li>
          ))}
        </ul>
      </details>
      <a
        href={supportUrl}
        className="mt-4 inline-block text-sm font-semibold text-primary underline underline-offset-4"
      >
        Open Support Request
      </a>
    </Card>
  );

  const reviewModal = offersReady && reviewOpen && offer && (
    <Modal
      isOpen
      onClose={onChangePeriod}
      closeDisabled={busy}
      title="Review Offer"
      className="dark:border-strokedark dark:bg-boxdark dark:text-bodydark1 dark:[&>header]:border-strokedark dark:[&>header>h2]:text-bodydark1 dark:[&>footer]:border-strokedark"
      footer={
        <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            variant="secondary"
            className={SECONDARY_BUTTON}
            disabled={busy}
            onClick={onChangePeriod}
          >
            Change Period
          </Button>
          <Button disabled={busy} onClick={onPurchase}>
            {busy
              ? 'Starting…'
              : Number(offer.payableAmountCents) === 0
                ? 'Use Balance and Start Analysis'
                : `Confirm ${money(offer.payableAmountCents)} and Start Analysis`}
          </Button>
        </div>
      }
    >
      <div className="space-y-5" aria-label="Analysis price breakdown">
        <div>
          <h3 className="text-lg font-semibold">
            Previous {offer.months} months
          </h3>
          <p className={`mt-1 text-xs ${MUTED}`}>
            ({historyRange(offer)}) · UTC
          </p>
          <p className={`mt-2 text-sm ${MUTED}`}>
            {number(offer.commitCount)} commits
          </p>
        </div>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt>Original Cost</dt>
            <dd className="shrink-0 tabular-nums">
              {money(offer.baseAmountCents)}
            </dd>
          </div>
          <div className="flex justify-between gap-4 text-success dark:text-meta-3">
            <dt>Discount ({offer.discountPercentage}%)</dt>
            <dd className="shrink-0 tabular-nums">
              {deduction(offer.discountAmountCents)}
            </dd>
          </div>
          <div className="flex justify-between gap-4 border-t border-stroke pt-3 font-semibold dark:border-strokedark">
            <dt>Discounted Cost</dt>
            <dd className="shrink-0 tabular-nums">
              {money(offer.subtotalCents)}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt>Balance Applied</dt>
            <dd className="shrink-0 tabular-nums">
              {deduction(offer.creditAppliedCents)}
            </dd>
          </div>
          <div className="flex justify-between gap-4 border-t border-stroke pt-3 text-lg font-bold dark:border-strokedark">
            <dt>Amount Due</dt>
            <dd className="shrink-0 tabular-nums">
              {money(offer.payableAmountCents)}
            </dd>
          </div>
        </dl>
        <dl className="space-y-2 rounded-lg bg-whiter p-4 text-sm dark:bg-meta-4">
          <div className="flex justify-between gap-4">
            <dt>Available Balance</dt>
            <dd className="shrink-0 tabular-nums">{money(creditCents)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt>Balance After Analysis</dt>
            <dd className="shrink-0 tabular-nums">
              {money(
                Math.max(0, creditCents - Number(offer.creditAppliedCents || 0))
              )}
            </dd>
          </div>
        </dl>
        <p
          className={`text-sm ${Number(offer.payableAmountCents) === 0 ? 'text-success dark:text-meta-3' : MUTED}`}
        >
          {Number(offer.payableAmountCents) === 0
            ? 'No additional payment required.'
            : 'One-time payment. Analysis starts after payment confirmation.'}
        </p>
      </div>
    </Modal>
  );

  return (
    <section
      className="mx-auto max-w-7xl space-y-6 font-satoshi text-black dark:text-bodydark1"
      aria-label="Historical Analysis"
    >
      <ol
        className="flex gap-2 border-b border-stroke pb-5 dark:border-strokedark sm:gap-4"
        aria-label="Historical Analysis steps"
        aria-hidden={reviewOpen || undefined}
      >
        {['Prepare', 'Choose period', 'Review', 'Analysis'].map(
          (label, index) => (
            <li
              key={label}
              aria-current={index === step ? 'step' : undefined}
              className={`flex min-w-0 flex-1 flex-col items-center gap-2 text-center text-xs sm:flex-row sm:text-left ${index === step ? 'font-semibold text-primary' : MUTED}`}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-semibold ${index === step ? 'bg-primary text-white' : index < step ? 'bg-success/10 text-success dark:text-meta-3' : 'border border-stroke bg-white dark:border-strokedark dark:bg-boxdark'}`}
              >
                {index < step ? '✓' : index + 1}
              </span>
              <span>{label}</span>
              {index < 3 && (
                <span
                  className="hidden h-px flex-1 bg-stroke dark:bg-strokedark sm:block"
                  aria-hidden="true"
                />
              )}
            </li>
          )
        )}
      </ol>

      {loading ? (
        <Card className={PANEL}>
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="mt-5 h-40 w-full" />
        </Card>
      ) : (
        <>
          {!request && (
            <Card className={`${PANEL} mx-auto max-w-3xl`} padding="lg">
              <h2 className="text-2xl font-semibold">
                See the history you can add
              </h2>
              <p className={`mt-3 text-sm leading-6 ${MUTED}`}>
                Scan your connected repositories once. Then choose from the
                available 3, 6, 12, and 24 month options.
              </p>
              {hasBalance && (
                <p className={`mt-4 text-sm ${MUTED}`}>
                  Available Historical Analysis balance:{' '}
                  <strong className="text-black dark:text-bodydark1">
                    {money(creditCents)}
                  </strong>
                </p>
              )}
              <ol className="my-6 list-decimal space-y-3 pl-5 text-sm">
                <li>We find your available repository history.</li>
                <li>You compare periods, discounts, and balance use.</li>
                <li>You review the amount due before starting analysis.</li>
              </ol>
              <Button disabled={busy} onClick={onPrepare}>
                {busy ? 'Preparing…' : 'Prepare offers'}
              </Button>
              <p className={`mt-3 text-xs ${MUTED}`}>
                Preparing offers does not charge a payment method or start
                analysis.
              </p>
            </Card>
          )}

          {preparing && (
            <Card className={PANEL} padding="lg" aria-live="polite">
              <h2 className="flex items-center gap-3 text-lg font-semibold">
                <span
                  aria-hidden="true"
                  className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent motion-reduce:animate-none"
                />
                {discoveringRepositories
                  ? 'Scanning repositories'
                  : 'Preparing your repository scan'}
              </h2>
              <p className={`mt-1 text-sm ${MUTED}`}>
                {discoveringRepositories
                  ? 'Finding the history available for your offers.'
                  : 'Getting your connected repositories ready. This may take a few minutes.'}
              </p>
              {discoveringRepositories && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold">
                      {number(discovery?.completed)} of{' '}
                      {number(discovery?.total)} repositories completed
                    </p>
                    <p className={`mt-1 text-sm ${MUTED}`}>
                      {number(discovery?.foundCommits)} commits found so far
                    </p>
                  </div>
                  {progressPercentage(
                    discovery?.completed,
                    discovery?.total
                  ) !== null && (
                    <span className="text-2xl font-bold tabular-nums">
                      {progressPercentage(
                        discovery?.completed,
                        discovery?.total
                      )}
                      %
                    </span>
                  )}
                </div>
              )}
              {renderProgress(
                discovery?.completed,
                discovery?.total,
                'Repositories scanned'
              )}
              {discovery?.activeRepo && (
                <p className={`mt-4 text-sm ${MUTED}`}>
                  Currently scanning{' '}
                  <strong className="text-black dark:text-bodydark1">
                    {discovery.activeRepo}
                  </strong>
                </p>
              )}
              {discovery?.repositories?.length > 0 && (
                <details className="mt-5">
                  <summary className="cursor-pointer text-sm font-semibold text-primary focus-visible:outline-primary">
                    Repository details
                  </summary>
                  <ul className="mt-3 divide-y divide-stroke dark:divide-strokedark">
                    {(discovery?.repositories || []).map(repo => (
                      <li
                        key={repo.repoUid || repo.repoName}
                        className="flex flex-wrap justify-between gap-3 py-3 text-sm"
                      >
                        <div className="min-w-0">
                          <p className="break-words font-medium">
                            {repo.repoName}
                          </p>
                          {(repo.retry || repo.error) && (
                            <p className="mt-1 text-xs text-warning-foreground dark:text-warning">
                              {repo.retry || repo.error}
                              {repo.retryCount > 0
                                ? ` · retry ${repo.retryCount}`
                                : ''}
                            </p>
                          )}
                        </div>
                        <span className={MUTED}>
                          {number(repo.foundCommits)} commits ·{' '}
                          {REPOSITORY_STATUSES[repo.status] || 'Waiting'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </Card>
          )}

          {request?.status === 'payment_failed' && (
            <Card
              className={`${PANEL} border-warning/30 bg-warning/5`}
              role="alert"
            >
              <h2 className="font-semibold">The previous payment failed</h2>
              <p className={`mt-1 text-sm ${MUTED}`}>
                {request.errorMessage ||
                  'Review the selected offer before trying payment again.'}
              </p>
            </Card>
          )}

          {offersReady && (
            <section
              aria-label="Available history offers"
              aria-hidden={reviewOpen || undefined}
              inert={reviewOpen ? '' : undefined}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold">
                    Choose your history window
                  </h2>
                  <p className={`mt-1 text-sm ${MUTED}`}>
                    Extend the history before your current analysis window.
                  </p>
                  <p className={`mt-2 text-xs ${MUTED}`}>
                    All dates are in UTC. Offer valid until{' '}
                    {expiry(request.expiresAt)}.
                  </p>
                </div>
                <div className="shrink-0 rounded-lg border border-stroke bg-white px-4 py-3 dark:border-strokedark dark:bg-boxdark">
                  <p className={`text-xs ${MUTED}`}>
                    Available Historical Analysis Balance
                  </p>
                  <p className="mt-1 text-xl font-semibold tabular-nums">
                    {money(creditCents)}
                  </p>
                </div>
              </div>
              <div
                className="mt-4 overflow-hidden rounded-xl border border-stroke bg-white dark:border-strokedark dark:bg-boxdark"
                role="radiogroup"
                aria-label="Historical analysis offers"
              >
                <table className="ha-offer-table text-sm" role="table">
                  <caption className="sr-only">
                    Available Historical Analysis periods and prices
                  </caption>
                  <thead className={`bg-whiter dark:bg-meta-4 ${MUTED}`}>
                    <tr>
                      <th scope="col">Period</th>
                      <th scope="col">Commit Count</th>
                      <th scope="col">Cost</th>
                      <th scope="col">Discount</th>
                      <th scope="col">Balance Applied</th>
                      <th scope="col">Amount Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(request.offers || []).map(item => (
                      <tr
                        key={item.months}
                        className={`border-t border-stroke dark:border-strokedark ${selectedMonths === item.months ? 'bg-primary/5 dark:bg-primary/15' : ''}`}
                      >
                        <td>
                          <label className="flex min-h-11 cursor-pointer items-start gap-3">
                            <input
                              className="mt-1 h-4 w-4 shrink-0 accent-primary focus-visible:outline-primary"
                              type="radio"
                              name={`${groupId}-period`}
                              aria-label={`${item.months} months`}
                              aria-describedby={`${groupId}-range-${item.months}`}
                              checked={selectedMonths === item.months}
                              disabled={busy || reviewOpen}
                              onChange={() => onSelect(item.months)}
                            />
                            <span>
                              <span className="block font-semibold">
                                Previous {item.months} months
                              </span>
                              <span
                                id={`${groupId}-range-${item.months}`}
                                className={`mt-1 block text-xs leading-5 ${MUTED}`}
                              >
                                ({historyRange(item)})
                              </span>
                            </span>
                          </label>
                        </td>
                        <td>
                          <span className={`ha-cell-label md:hidden ${MUTED}`}>
                            Commit Count
                          </span>
                          <span className="tabular-nums">
                            {number(item.commitCount)}
                          </span>
                        </td>
                        <td>
                          <span className={`ha-cell-label md:hidden ${MUTED}`}>
                            Cost
                          </span>
                          {item.discountAmountCents > 0 ? (
                            <s className={`tabular-nums ${MUTED}`}>
                              {money(item.baseAmountCents)}
                            </s>
                          ) : (
                            <span className="tabular-nums">
                              {money(item.baseAmountCents)}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`ha-cell-label md:hidden ${MUTED}`}>
                            Discount
                          </span>
                          <span
                            className={
                              item.discountAmountCents > 0
                                ? 'text-success dark:text-meta-3'
                                : MUTED
                            }
                          >
                            <span className="font-semibold">
                              {item.discountPercentage}%
                            </span>{' '}
                            <span className="whitespace-nowrap text-xs tabular-nums">
                              ({deduction(item.discountAmountCents)})
                            </span>
                          </span>
                        </td>
                        <td>
                          <span className={`ha-cell-label md:hidden ${MUTED}`}>
                            Balance Applied
                          </span>
                          <span className="tabular-nums">
                            {deduction(item.creditAppliedCents)}
                          </span>
                        </td>
                        <td>
                          <span className={`ha-cell-label md:hidden ${MUTED}`}>
                            Amount Due
                          </span>
                          <span className="font-semibold tabular-nums">
                            {money(item.payableAmountCents)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className={`text-xs ${MUTED}`}>
                  Discounts and balance are deducted from the original cost.
                  Selecting a period does not start analysis.
                </p>
                <Button
                  className="shrink-0"
                  disabled={!offer || busy || reviewOpen}
                  onClick={onReview}
                >
                  Review Offer
                </Button>
              </div>
            </section>
          )}
          {reviewModal}

          {paymentPending && (
            <Card
              className={`${PANEL} mx-auto max-w-3xl`}
              padding="lg"
              aria-live="polite"
            >
              <h2 className="text-xl font-semibold">
                {request.payment?.state === 'checkout_required'
                  ? 'Continue to secure checkout'
                  : 'Confirming payment'}
              </h2>
              <p className={`mt-3 text-sm leading-6 ${MUTED}`}>
                {request.payment?.state === 'outcome_unknown'
                  ? 'Paddle is still confirming the result. Do not retry the purchase while the payment outcome is unknown.'
                  : request.payment?.state === 'checkout_required'
                    ? 'Your saved payment method could not be used. Complete the payment in Paddle Checkout to continue.'
                    : 'Your payment has been submitted. Analysis will start after payment confirmation.'}
              </p>
              {request.payment?.checkoutUrl &&
                request.payment?.state !== 'outcome_unknown' && (
                  <Button
                    className="mt-5"
                    onClick={() =>
                      window.location.assign(request.payment.checkoutUrl)
                    }
                  >
                    Continue to Secure Checkout
                  </Button>
                )}
              <p className={`mt-4 text-xs ${MUTED}`}>
                We will update this status when confirmation arrives.
              </p>
            </Card>
          )}

          {processing && (
            <Card className={PANEL} padding="lg" aria-live="polite">
              <h2 className="text-xl font-semibold">
                {request.status === 'processing_queued'
                  ? 'Your analysis is queued'
                  : 'Historical analysis is running'}
              </h2>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-semibold">
                    {number(completed)} of {number(total)} commits completed
                  </p>
                  <p className={`mt-1 text-sm ${MUTED}`}>
                    <span
                      className={
                        failed > 0 ? 'text-danger dark:text-meta-7' : ''
                      }
                    >
                      {number(failed)} commits failed
                    </span>{' '}
                    · {number(Math.max(0, total - resolved))} still processing
                  </p>
                </div>
                {progressPercentage(resolved, total) !== null && (
                  <span className="text-2xl font-bold tabular-nums">
                    {progressPercentage(resolved, total)}%
                  </span>
                )}
              </div>
              {renderProgress(resolved, total, 'Commit processing resolved')}
              <p className={`mt-3 text-xs ${MUTED}`}>
                Progress includes completed and failed commits.
              </p>
              {phases.length > 0 && (
                <ol className="mt-6 divide-y divide-stroke dark:divide-strokedark">
                  {phases.map((phase, index) => (
                    <li key={phase.label} className="py-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 className="text-sm font-semibold">
                          <span className={`mr-2 ${MUTED}`}>{index + 1}.</span>
                          {phase.label}
                        </h3>
                        <p className={`text-xs tabular-nums ${MUTED}`}>
                          {number(phase.current)} of {number(phase.total)}{' '}
                          commits {phase.unit}
                        </p>
                      </div>
                      {renderProgress(
                        phase.current,
                        phase.total,
                        `${phase.label}: commits ${phase.unit}`
                      )}
                    </li>
                  ))}
                </ol>
              )}
              {phases.length > 0 && (
                <p className={`mt-3 text-xs ${MUTED}`}>
                  Pipeline stages may overlap. Submitted commits have been sent
                  for analysis; they are not necessarily completed.
                </p>
              )}
              {(pipeline?.commitResults || []).length > 0 && (
                <details className="mt-5">
                  <summary className="cursor-pointer text-sm font-semibold text-primary focus-visible:outline-primary">
                    Recent commit results
                  </summary>
                  <ul className="mt-3 divide-y divide-stroke text-xs dark:divide-strokedark">
                    {pipeline.commitResults.map(item => (
                      <li
                        key={`${item.repoUid}:${item.commitHash}`}
                        className="flex justify-between gap-4 py-2"
                      >
                        <code className="min-w-0 break-all">
                          {item.commitHash}
                        </code>
                        <span
                          className={`shrink-0 font-semibold ${item.status === 'failed' ? 'text-danger dark:text-meta-7' : 'text-success dark:text-meta-3'}`}
                        >
                          {item.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </Card>
          )}

          {['estimate_failed', 'no_offers', 'expired'].includes(
            request?.status
          ) && (
            <Card className={PANEL} padding="lg" role="alert">
              <h2 className="text-xl font-semibold">
                {request.status === 'no_offers'
                  ? allRepositoriesExcluded
                    ? 'No repositories could be scanned'
                    : 'No additional history was found'
                  : request.status === 'expired'
                    ? 'This offer has expired'
                    : 'Your offer could not be prepared'}
              </h2>
              <p className={`mt-3 text-sm ${MUTED}`}>
                {request.errorMessage ||
                  (request.status === 'no_offers'
                    ? allRepositoriesExcluded
                      ? 'We could not access any connected repository history. Review the excluded repositories or contact support, then prepare new offers.'
                      : 'There is no additional accessible repository history to include in this offer.'
                    : request.status === 'expired'
                      ? 'Prepare a new offer to see the current history scope and price.'
                      : 'We could not finish scanning your repository history. Prepare a new offer to try again.')}
              </p>
              {request.failure?.repoName && (
                <p className="mt-3 text-sm">
                  Repository: {request.failure.repoName}
                  {request.failure.retryable ? ' · retry scheduled' : ''}
                </p>
              )}
              <Button className="mt-5" disabled={busy} onClick={onPrepare}>
                {busy ? 'Preparing…' : 'Prepare new offers'}
              </Button>
            </Card>
          )}

          {request?.status === 'processing_failed' && (
            <Card className={PANEL} padding="lg" role="alert">
              <h2 className="text-xl font-semibold">
                Your analysis needs attention
              </h2>
              <p className={`mt-3 text-sm ${MUTED}`}>
                {request.errorMessage ||
                  'We could not finish processing your selected history. Contact support to review this analysis.'}
              </p>
              <p className="mt-4 text-sm">
                {number(completed)} commits completed · {number(failed)} failed
              </p>
              <a
                href={`/settings/support?${new URLSearchParams({ topic: 'historical_analysis', message: `Please help us review our Historical Analysis. Reference: ${request.id}` }).toString()}`}
                className="mt-5 inline-flex min-h-10 items-center rounded-lg bg-primary px-4 text-sm font-medium text-white focus-visible:outline-primary"
              >
                Contact support
              </a>
            </Card>
          )}

          {request?.status === 'completed' && (
            <Card className={`${PANEL} mx-auto max-w-3xl`} padding="lg">
              <p className="text-sm font-semibold text-success dark:text-meta-3">
                Historical analysis completed
              </p>
              <h2 className="mt-2 text-2xl font-semibold">
                Your earlier history is ready
              </h2>
              <p className={`mt-3 text-sm ${MUTED}`}>
                Your selected repository history is now included in GitMe
                reports.
              </p>
              {total > 0 && (
                <p className="mt-4 text-sm">
                  {number(completed)} commits completed
                  {failed > 0 && (
                    <span className="text-danger dark:text-meta-7">
                      {' '}
                      · {number(failed)} failed
                    </span>
                  )}
                </p>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="/"
                  className="inline-flex min-h-10 items-center rounded-lg bg-primary px-4 text-sm font-medium text-white focus-visible:outline-primary"
                >
                  View reports
                </a>
                <Button
                  variant="secondary"
                  className={SECONDARY_BUTTON}
                  disabled={busy}
                  onClick={onPrepare}
                >
                  {busy ? 'Preparing…' : 'Prepare new offers'}
                </Button>
              </div>
            </Card>
          )}
          {excludedNotice}
        </>
      )}
    </section>
  );
};

HistoricalAnalysisV2View.propTypes = {
  loading: PropTypes.bool.isRequired,
  request: PropTypes.object,
  offer: PropTypes.object,
  creditCents: PropTypes.number.isRequired,
  selectedMonths: PropTypes.number,
  reviewOpen: PropTypes.bool.isRequired,
  busy: PropTypes.bool.isRequired,
  excludedRepositories: PropTypes.arrayOf(PropTypes.object).isRequired,
  supportUrl: PropTypes.string.isRequired,
  onPrepare: PropTypes.func.isRequired,
  onPurchase: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onReview: PropTypes.func.isRequired,
  onChangePeriod: PropTypes.func.isRequired
};

export default HistoricalAnalysisV2View;
